// A deliberately small Markdown subset, enough for release notes: headings, paragraphs, bulleted
// and numbered lists (one level), **bold**, *italic*, `code` and [links](https://…). Anything else
// stays plain text. Rendered by components/Markdown.tsx.

export type MarkdownInline =
    | { type: 'text'; text: string }
    | { type: 'strong'; children: MarkdownInline[] }
    | { type: 'em'; children: MarkdownInline[] }
    | { type: 'code'; text: string }
    | { type: 'link'; href: string; children: MarkdownInline[] };

export type MarkdownBlock =
    | { type: 'heading'; level: number; children: MarkdownInline[] }
    | { type: 'paragraph'; children: MarkdownInline[] }
    | { type: 'list'; ordered: boolean; items: MarkdownInline[][] };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET_ITEM = /^[-*+]\s+(.*)$/;
const NUMBERED_ITEM = /^\d+[.)]\s+(.*)$/;

// The leftmost match wins; at the same position the alternatives are tried in this order.
// Italic needs non-space next to both asterisks, so `2 * 3 * 4` stays text.
const INLINE = /`([^`]+)`|\*\*(.+?)\*\*|\*([^*\s](?:[^*]*[^*\s])?)\*|\[([^\]]+)\]\(([^)\s]+)\)/;
const SAFE_HREF = /^(https?:|mailto:)/i;

function pushText(nodes: MarkdownInline[], text: string) {
    if (!text) return;
    const last = nodes[nodes.length - 1];
    if (last?.type === 'text') last.text += text; else nodes.push({type: 'text', text});
}

export function parseInline(text: string): MarkdownInline[] {
    const nodes: MarkdownInline[] = [];
    let rest = text;
    while (rest) {
        const match = INLINE.exec(rest);
        if (!match) {
            pushText(nodes, rest);
            break;
        }
        pushText(nodes, rest.slice(0, match.index));
        const [whole, code, strong, em, linkText, href] = match;
        if (code !== undefined) nodes.push({type: 'code', text: code});
        else if (strong !== undefined) nodes.push({type: 'strong', children: parseInline(strong)});
        else if (em !== undefined) nodes.push({type: 'em', children: parseInline(em)});
        else if (SAFE_HREF.test(href)) nodes.push({type: 'link', href, children: parseInline(linkText)});
        else pushText(nodes, linkText); // e.g. javascript: links lose the link, keep the text
        rest = rest.slice(match.index + whole.length);
    }
    return nodes;
}

export function parseMarkdown(source: string): MarkdownBlock[] {
    const blocks: MarkdownBlock[] = [];
    let paragraph: string[] = [];
    let list: { ordered: boolean; items: string[] } | undefined;
    // A blank line ends a list item: the next line is either another item or a new paragraph.
    let afterBlank = false;

    const endParagraph = () => {
        if (paragraph.length) blocks.push({type: 'paragraph', children: parseInline(paragraph.join(' '))});
        paragraph = [];
    };
    const endList = () => {
        if (list) blocks.push({type: 'list', ordered: list.ordered, items: list.items.map(parseInline)});
        list = undefined;
    };

    for (const rawLine of source.split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!line) {
            endParagraph();
            afterBlank = true;
            continue;
        }
        const heading = HEADING.exec(line);
        const bullet = BULLET_ITEM.exec(line);
        const item = bullet ?? NUMBERED_ITEM.exec(line);
        if (heading) {
            endParagraph();
            endList();
            blocks.push({type: 'heading', level: heading[1].length, children: parseInline(heading[2].trim())});
        } else if (item) {
            const ordered = !bullet;
            endParagraph();
            if (list?.ordered !== ordered) endList();
            list ??= {ordered, items: []};
            list.items.push(item[1].trim());
        } else if (list && !afterBlank) {
            // A wrapped line continues the list item above it.
            list.items[list.items.length - 1] += ` ${line}`;
        } else {
            endList();
            paragraph.push(line);
        }
        afterBlank = false;
    }
    endParagraph();
    endList();
    return blocks;
}
